/**
 * BasicInfoDrawer (wandering profile) — slide-out edit form.
 *
 * Edits the three V1-supported fields: description, badge serial number
 * (manufacture_uuid), and assigned resident. Device name and model are
 * read-only on this page so they're not surfaced here.
 *
 * Reuses the shared dp-drawer chrome (dp-drawer, dp-drawer__field, etc.)
 * already loaded by sibling profile pages.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import SearchableSelect from '../../../../components/v2/SearchableSelect.jsx';
import { areaOptions, residentOptions } from '../../components/advLearnIn.js';

const BasicInfoDrawer = ({
  open,
  onClose,
  onSave,
  saving,
  description: initDesc,
  serialNum: initSerial,
  residentId: initResidentId,
  residents,
}) => {
  const [description, setDescription] = useState('');
  const [serialNum, setSerialNum] = useState('');
  const [residentId, setResidentId] = useState(0);
  const [errors, setErrors] = useState({});

  // Populate form on each open transition (false → true). Avoids stomping
  // the user's edits if a polling refresh fires while the drawer is open.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setDescription(initDesc || '');
      setSerialNum(initSerial || '');
      setResidentId(initResidentId || 0);
      setErrors({});
    }
    prevOpen.current = open;
  });

  // Escape closes the drawer (matches sibling drawer behavior).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const validate = useCallback(() => {
    const errs = {};
    if (!serialNum || !serialNum.trim()) {
      errs.serialNum = 'Badge number is required.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [serialNum]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      description: description.trim(),
      serialNum: serialNum.trim(),
      residentId: residentId || 0,
    });
  }, [validate, onSave, description, serialNum, residentId]);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`} role="dialog" aria-modal="true" aria-label="Edit wandering device">
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Edit Device Info</h2>
        <button type="button" className="dp-drawer__close" onClick={onClose} aria-label="Close">{'✕'}</button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* Description */}
        <div className="dp-drawer__field wp-form-group">
          <label className="dp-drawer__label" htmlFor="wp-desc">Description</label>
          <input
            id="wp-desc"
            type="text"
            className="dp-drawer__input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description for this device"
            autoFocus
          />
        </div>

        {/* Badge / Serial Number */}
        <div className="dp-drawer__field wp-form-group">
          <label className="dp-drawer__label" htmlFor="wp-serial">
            Badge Number <span className="dp-drawer__required">*</span>
          </label>
          <input
            id="wp-serial"
            type="text"
            className={`dp-drawer__input${errors.serialNum ? ' is-invalid' : ''}`}
            value={serialNum}
            onChange={(e) => setSerialNum(e.target.value)}
            placeholder="Number printed on the physical badge"
          />
          {errors.serialNum ? (
            <p className="dp-drawer__error">{errors.serialNum}</p>
          ) : null}
        </div>

        {/* Assigned Resident — V2 SearchableSelect, matching the Add Resident
            drawer's area picker (see the device-profile drawer for why). No
            asterisk: clearing this is the documented way to unassign the badge
            without deactivating it, so an empty value is a valid save. */}
        <div className="dp-drawer__field wp-form-group">
          <label htmlFor="wp-edit-resident" className="dp-drawer__label">Assigned Resident</label>
          <SearchableSelect
            id="wp-edit-resident"
            options={residentOptions(residents)}
            value={residentId}
            /* Numeric state, 0 = unassigned — onSave forwards `residentId || 0`. */
            onChange={(val) => setResidentId(val ? Number(val) : 0)}
            placeholder="Search or select resident..."
            ariaLabel="Assigned Resident"
            isClearable
          />
          <p className="wp-readonly-hint">Clear the selection to unassign without deactivating the badge.</p>
        </div>

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BasicInfoDrawer;
